<template>
  <div class="flex flex-col gap-y-4">
    <div>
      <h3>
        <Translation
          name="calculation_wizard.preview.sidebar_bonuses"
          default="Bonuses"
        />
      </h3>
      <div class="rounded-xl bg-[#3B3B44] p-4">
        <div class="flex flex-col gap-y-2 mb-6">
          <span class="text-white font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_you_entered"
              default="You entered"
            />
          </span>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_extra_payment"
                default="13/14/15 month payment:"
              />
            </span>
            <div class="value-tag">
              {{
                thirteenFourteenFifteenMonthPayment
                  ? thirteenFourteenFifteenMonthPayment.toFixed(2)
                  : "-"
              }}
              {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_time_dependent_bonuses"
                default="Time-dependent bonuses:"
              />
            </span>
            <div class="value-tag">
              {{ timeDependentBonuses ? timeDependentBonuses.toFixed(2) : "-" }}
              {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_non_time_dependent_bonuses"
                default="Non-time dependent bonuses:"
              />
            </span>
            <div class="value-tag">
              {{
                nonTimeDependentBonuses
                  ? nonTimeDependentBonuses.toFixed(2)
                  : "-"
              }}
              {{ currency }}
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-y-2">
          <span class="text-white font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_chart_shows"
              default="The chart shows"
            />
          </span>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_monthly_avg_bonuses"
                default="Comparable monthly average bonus:"
              />
            </span>
            <div class="value-tag">
              {{
                comparableMonthlyAverageBonuses
                  ? comparableMonthlyAverageBonuses.toFixed(2)
                  : "-"
              }}
              {{ currency }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasBonuses">
      <h3>
        <Translation
          name="calculation_wizard.preview.sidebar_calculation"
          default="Calculation"
        />
      </h3>
      <div class="flex flex-col gap-y-2">
        <!-- Step 1: Share of year worked calculation -->
        <div id="bonuses-step-1" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(1)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_1"
                default="Step 1"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[1] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[1] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_1_p1"
                default="The tool first calculates the <strong>share of the year</strong> the employee has worked."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_1_p2"
                  default="For this job category or individual, the <strong>share of year worked</strong> is:"
                />
              </span>
              <div class="value-tag">
                {{
                  rawShareOfYearWorked
                    ? rawShareOfYearWorked.toFixed(2)
                    : shareOfYearWorked
                }}%
              </div>
            </div>
            <p class="mt-3">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_1_see_wages"
                default="See 'wages' for more details on this calculation"
              />
            </p>

            <!-- Show correction notice when overtime exceeded -->
            <div
              v-if="overtimeHoursExceeded"
              class="bg-orange-900/30 border border-orange-600 rounded p-3 mt-3"
            >
              <p class="text-orange-300 text-sm">
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_1_correction"
                  default="The tool corrects this to:"
                />
                <strong class="ml-1">100%</strong>
              </p>
            </div>
          </div>
        </div>

        <!-- Step 2: Adjust time-dependent bonuses -->
        <div id="bonuses-step-2" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(2)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_2"
                default="Step 2"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[2] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[2] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_2_p1"
                default="The tool adjusts time-dependent bonuses for the share of year worked."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_2_result"
                  default="For this job category or individual, 'Comparable annual time-dependent bonuses' is:"
                />
              </span>
              <div class="value-tag">
                {{
                  comparableAnnualTimeDependentBonuses
                    ? comparableAnnualTimeDependentBonuses.toFixed(2)
                    : "-"
                }}
                {{ currency }}
              </div>
            </div>
            <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
              <p class="text-xs text-gray-300 mb-2">
                <Translation
                  name="calculation_wizard.preview.sidebar_calculation"
                  default="Calculation"
                />:
              </p>
              <!-- When overtime exceeded (Step 2b): Use "Corrected" labels -->
              <template v-if="overtimeHoursExceeded">
                <p class="text-sm text-gray-200">
                  (
                  <Translation
                    name="calculation_wizard.preview.sidebar_bonuses_step_2b_corrected_13"
                    default="Corrected 13/14/15th month payment:"
                  />
                  <strong>{{
                    thirteenFourteenFifteenMonthPayment
                      ? thirteenFourteenFifteenMonthPayment.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_bonuses_step_2b_corrected_other"
                    default="Corrected Other time-dependent bonuses:"
                  />
                  <strong>{{
                    timeDependentBonuses ? timeDependentBonuses.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                  )
                </p>
                <p class="text-sm text-gray-200">
                  ÷
                  <Translation
                    name="calculation_wizard.preview.sidebar_share_worked"
                    default="Share of the year worked:"
                  />
                  <strong
                    >{{
                      shareOfYearWorked
                        ? Number(shareOfYearWorked).toFixed(2)
                        : "-"
                    }}%</strong
                  >
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_bonuses_step_2_result_label"
                    default="Comparable annual time-dependent bonuses:"
                  />
                  <strong>{{
                    comparableAnnualTimeDependentBonuses
                      ? comparableAnnualTimeDependentBonuses.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </template>
              <!-- When not overtime (Step 2a): Use normal labels -->
              <template v-else>
                <p class="text-sm text-gray-200">
                  (
                  <Translation
                    name="calculation_wizard.preview.sidebar_bonuses_step_2_13_month"
                    default="13/14/15th month payment:"
                  />
                  <strong>{{
                    thirteenFourteenFifteenMonthPayment
                      ? thirteenFourteenFifteenMonthPayment.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_bonuses_step_2_other"
                    default="Other time-dependent bonuses:"
                  />
                  <strong>{{
                    timeDependentBonuses ? timeDependentBonuses.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                  )
                </p>
                <p class="text-sm text-gray-200">
                  ÷
                  <Translation
                    name="calculation_wizard.preview.sidebar_share_worked"
                    default="Share of the year worked:"
                  />
                  <strong
                    >{{
                      shareOfYearWorked
                        ? Number(shareOfYearWorked).toFixed(2)
                        : "-"
                    }}%</strong
                  >
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_bonuses_step_2_result_label"
                    default="Comparable annual time-dependent bonuses:"
                  />
                  <strong>{{
                    comparableAnnualTimeDependentBonuses
                      ? comparableAnnualTimeDependentBonuses.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </template>
            </div>
            <!-- Note only for overtime case (Step 2b) -->
            <p v-if="overtimeHoursExceeded" class="mt-3 text-sm text-gray-300">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_2b_note"
                default="Note that overtime hours, and bonuses earned during those hours, are not included in the calculation of the comparable remuneration. The bonuses you entered are corrected to exclude overtime bonuses since they were earned during hours above the legal maximum."
              />
            </p>
          </div>
        </div>
        <!-- Step 3: Add non-time dependent bonuses -->
        <div id="bonuses-step-3" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(3)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_3"
                default="Step 3"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[3] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[3] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_3_p1"
                default="Non-time dependent bonuses don’t have to be adjusted for share of year worked. They are added to the adjusted time-dependent bonuses to calculate the total."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_3_result"
                  default="For this job category or individual, 'Total comparable annual bonuses' is:"
                />
              </span>
              <div class="value-tag">
                {{
                  totalComparableAnnualBonuses
                    ? totalComparableAnnualBonuses.toFixed(2)
                    : "-"
                }}
                {{ currency }}
              </div>
            </div>
            <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
              <p class="text-xs text-gray-300 mb-2">
                <Translation
                  name="calculation_wizard.preview.sidebar_calculation"
                  default="Calculation"
                />:
              </p>
              <p class="text-sm text-gray-200">
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_3_calculation_1"
                  default="Comparable annual time-dependent bonuses:"
                />
                <strong>{{
                  comparableAnnualTimeDependentBonuses
                    ? comparableAnnualTimeDependentBonuses.toFixed(2)
                    : "-"
                }}</strong>
                {{ currency }}
              </p>
              <p class="text-sm text-gray-200">
                +
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_3_calculation_2"
                  default="Non time-dependent bonuses:"
                />
                <strong>{{
                  nonTimeDependentBonuses
                    ? nonTimeDependentBonuses.toFixed(2)
                    : "-"
                }}</strong>
                {{ currency }}
              </p>
              <hr class="my-2 border-gray-600" />
              <p class="text-sm text-gray-200">
                =
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_3_calculation_3"
                  default="Total comparable annual bonuses:"
                />
                <strong>{{
                  totalComparableAnnualBonuses
                    ? totalComparableAnnualBonuses.toFixed(2)
                    : "-"
                }}</strong>
                {{ currency }}
              </p>
            </div>
          </div>
        </div>
        <!-- Step 4: Divide by 12 months -->
        <div id="bonuses-step-4" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(4)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_4"
                default="Step 4"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[4] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[4] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_bonuses_step_4_p1"
                default="To be able to compare to a living wage estimate, the annual value of bonuses is divided by 12 months."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_4_result"
                  default="For this job category or individual, 'Comparable monthly full-time bonuses' is:"
                />
              </span>
              <div class="value-tag">
                {{
                  comparableMonthlyAverageBonuses
                    ? comparableMonthlyAverageBonuses.toFixed(2)
                    : "-"
                }}
                {{ currency }}
              </div>
            </div>
            <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
              <p class="text-xs text-gray-300 mb-2">
                <Translation
                  name="calculation_wizard.preview.sidebar_calculation"
                  default="Calculation"
                />:
              </p>
              <p class="text-sm text-gray-200">
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_4_calculation_1"
                  default="Total comparable annual bonuses:"
                />
                <strong>{{
                  totalComparableAnnualBonuses
                    ? totalComparableAnnualBonuses.toFixed(2)
                    : "-"
                }}</strong>
                {{ currency }}
              </p>
              <p class="text-sm text-gray-200">
                ÷ 12
                <Translation
                  name="calculation_wizard.preview.sidebar_months"
                  default="months"
                />
              </p>
              <hr class="my-2 border-gray-600" />
              <p class="text-sm text-gray-200">
                =
                <Translation
                  name="calculation_wizard.preview.sidebar_bonuses_step_4_calculation_2"
                  default="Comparable monthly full-time bonuses:"
                />
                <strong>{{
                  comparableMonthlyAverageBonuses
                    ? comparableMonthlyAverageBonuses.toFixed(2)
                    : "-"
                }}</strong>
                {{ currency }}
              </p>
            </div>
            <p class="mt-3">
              <Translation
                name="calculation_wizard.preview.sidebar_step_final_note"
                default="This number is displayed in your chart."
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from "vue";
import ChevronDownWhite from "../../icons/ChevronDownWhite.vue";

const props = defineProps({
  currency: {
    type: String,
    required: true,
  },
  // User input values
  thirteenFourteenFifteenMonthPayment: {
    type: Number,
    required: true,
  },
  timeDependentBonuses: {
    type: Number,
    required: true,
  },
  nonTimeDependentBonuses: {
    type: Number,
    required: true,
  },
  shareOfYearWorked: {
    type: Number,
    required: true,
  },
  comparableAnnualTimeDependentBonuses: {
    type: Number,
    required: true,
  },
  totalComparableAnnualBonuses: {
    type: Number,
    required: true,
  },
  comparableMonthlyAverageBonuses: {
    type: Number,
    required: true,
  },
  jobCategory: {
    type: String,
    default: "",
  },
  gender: {
    type: String,
    default: "",
  },
  // Additional props for overtime detection
  rawShareOfYearWorked: {
    type: Number,
    default: 0,
  },
  overtimeHoursExceeded: {
    type: Boolean,
    default: false,
  },
});

const hasBonuses = computed(() => {
  return (
    (props.thirteenFourteenFifteenMonthPayment || 0) > 0 ||
    (props.timeDependentBonuses || 0) > 0 ||
    (props.nonTimeDependentBonuses || 0) > 0
  );
});

const stepsOpen = ref({
  1: true,
  2: false,
  3: false,
  4: false,
});

const toggleStep = (step) => {
  const accordion = document.getElementById(`bonuses-step-${step}`);
  if (!accordion) return;
  const accordionContent = accordion.querySelector(".accordion-content");
  if (!accordionContent) return;
  if (stepsOpen.value[step]) {
    accordionContent.style.maxHeight = 0;
  } else {
    accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
  }
  stepsOpen.value[step] = !stepsOpen.value[step];
};

onMounted(() => {
  const accordion = document.getElementById("bonuses-step-1");
  if (accordion) {
    const accordionContent = accordion.querySelector(".accordion-content");
    if (accordionContent) {
      accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
    }
  }
});
</script>
